import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import PolicyPage, { PolicyContactBox } from "./PolicyPage";
import "../css/privacy.scss";

export default function Privacy() {
  return (
    <PolicyPage
      activePath="/privacy"
      eyebrow="Privacy"
      title="Privacy notice"
      intro="What personal information this storefront uses, why it is used, who may receive it and the choices available to you."
      note={{
        title: "Current storefront scope",
        content:
          "Cart and wishlist data stays in browser storage. The contact form currently validates locally and does not transmit its fields. Continuing from checkout sends limited order and contact data to the store server and then redirects to a hosted payment page; card details are entered with that provider.",
      }}
      sections={[
        {
          id: "privacy-controller",
          shortTitle: "Who is responsible",
          title: "Who is responsible for your information",
          content: (
            <>
              <p>
                {siteConfig.brandName} is the trading name displayed by this storefront.
                Privacy questions and requests can be sent to {siteConfig.contact.email}.
                The project does not currently specify a separate registered legal entity;
                the <Link to="/legal-notice">legal notice</Link> records the operator
                information presently available.
              </p>
              <p>
                A hosted payment provider acts under its own privacy notice for the card
                and authentication information entered on its page. Other suppliers may
                process information on our instructions where required to run a live store.
              </p>
            </>
          ),
        },
        {
          id: "privacy-information",
          shortTitle: "Information collected",
          title: "Information we may collect",
          content: (
            <div className="policy-definition-list">
              <div>
                <strong>Order &amp; contact</strong>
                <p>Name, email, delivery details, products, quantities, order reference and status.</p>
              </div>
              <div>
                <strong>Payment metadata</strong>
                <p>Amount, currency, provider reference, authorisation and refund status. Full card details are entered with the hosted provider.</p>
              </div>
              <div>
                <strong>Support</strong>
                <p>Messages, attachments, complaint details and the steps taken to respond.</p>
              </div>
              <div>
                <strong>Device &amp; usage</strong>
                <p>IP address, browser, device, page requests, timestamps, security events and cookie or storage preferences.</p>
              </div>
              <div>
                <strong>Browser selections</strong>
                <p>Product identifiers, options and quantities saved locally for the cart and wishlist.</p>
              </div>
            </div>
          ),
        },
        {
          id: "privacy-sources",
          shortTitle: "Sources",
          title: "Where information comes from",
          content: (
            <>
              <p>
                Information comes directly from entries and actions made on the site,
                from the browser and device used to access it, and from payment, hosting,
                fraud-prevention or delivery providers involved in a transaction.
              </p>
              <p>
                If someone orders for another recipient, the customer must be authorised
                to provide that person’s delivery details and should direct them to this
                notice where appropriate.
              </p>
            </>
          ),
        },
        {
          id: "privacy-use",
          shortTitle: "Purposes & legal bases",
          title: "Why information is used",
          content: (
            <>
              <p>Information may be used to:</p>
              <ul>
                <li>validate checkout, take payment and fulfil or administer an order;</li>
                <li>deliver customer care, returns, refunds, warranty support and complaints;</li>
                <li>secure the site, prevent fraud and diagnose errors;</li>
                <li>keep financial, tax, consumer and legal records;</li>
                <li>measure and improve the store where lawful controls are in place; and</li>
                <li>send marketing only where a valid permission or other lawful basis exists.</li>
              </ul>
              <p>
                Depending on the activity and location, the legal basis may be performing
                a contract, taking requested pre-contract steps, complying with law,
                consent, or a proportionate legitimate interest such as security and
                service improvement. Consent can be withdrawn for future processing.
              </p>
            </>
          ),
        },
        {
          id: "privacy-sharing",
          shortTitle: "Sharing & transfers",
          title: "Service providers, authorities & transfers",
          content: (
            <>
              <p>
                Necessary information may be shared with hosting, payment, fraud-screening,
                email, customer-support, fulfilment, professional-adviser and delivery
                providers. It may also be disclosed when required by law, to protect legal
                rights, or as part of a genuine business reorganisation subject to
                appropriate safeguards.
              </p>
              <p>
                Personal information is not stated to be sold. A live operator should
                contractually limit each provider to the information needed for its role.
                Where information crosses borders, recognised contractual, adequacy or
                other lawful safeguards should be used as required.
              </p>
            </>
          ),
        },
        {
          id: "privacy-storage",
          shortTitle: "Storage, security & retention",
          title: "Browser storage, security & retention",
          content: (
            <>
              <p>
                The current cart and wishlist are saved in local storage on the device.
                Clear the items in the interface or remove this site’s data through browser
                settings to erase them. See the <Link to="/cookies">cookie policy</Link> for
                more detail.
              </p>
              <p>
                Reasonable technical and organisational safeguards should protect live
                order and support records. No internet service is risk-free. Information
                is kept only as long as needed for the purpose collected, including order
                fulfilment, legal recordkeeping, fraud prevention and dispute periods, then
                deleted or anonymised where feasible.
              </p>
            </>
          ),
        },
        {
          id: "privacy-rights",
          shortTitle: "Your rights",
          title: "Your choices & rights",
          content: (
            <>
              <p>
                Depending on applicable law, you may request access, correction, deletion,
                restriction or portability, object to certain processing, withdraw consent
                and complain to the competent regulator. Verification may be required before
                a request is fulfilled.
              </p>
              <p>
                The <Link to="/privacy-choices">privacy choices page</Link> explains how to
                submit a request and how to control browser-stored cart and wishlist data.
                We do not knowingly direct this store to children or knowingly collect
                children’s data without any consent required by law.
              </p>
            </>
          ),
        },
        {
          id: "privacy-contact",
          shortTitle: "Contact & changes",
          title: "Questions, complaints & policy changes",
          content: (
            <>
              <p>
                Material updates will be reflected by the revised date. If a change
                significantly affects how existing information is used, additional notice
                or consent will be provided where required.
              </p>
              <p>
                Include “Privacy” in the subject line when contacting us. Do not email card
                details, passwords or identity documents unless a secure method has first
                been arranged.
              </p>
              <PolicyContactBox label="Privacy question?" linkLabel="Email privacy support" />
            </>
          ),
        },
      ]}
    />
  );
}
